import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RcoSplitStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-model/advanced-flow/common";
import {
  CheckoutAbTestMetricToOptimize,
  CheckoutAbTestMetricToOptimizeLabelMap,
} from "@redotech/redo-model/checkouts/checkout-ab-test-builder";
import { isTrustedAdminCheckoutOptimizationUser } from "@redotech/redo-model/checkouts/trusted-users";
import {
  checkoutOptimizationTreatmentStorefrontFeaturesSchema,
  StorefrontFeatures,
} from "@redotech/redo-model/split";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import { ObjectId } from "bson";
import { memo, useContext, useMemo, useState } from "react";
import { PartialFlowStep, PartialWithRequiredType } from "..";
import { FlowInProgress } from "../../flow-editor";
import { deleteElementAndAllChildren } from "../../utils/delete-element";
import {
  SplitVariantControls,
  SplitVariantControlsProps,
} from "../common/split-variant-controls";
import { TreatmentStorefrontFeaturesConfiguration } from "./treatment-storefront-features-configuration";

interface RcoSplitEditInspectorProps {
  step: PartialWithRequiredType<RcoSplitStep>;
  steps: PartialFlowStep[];
  setStep: (step: PartialWithRequiredType<RcoSplitStep>) => void;
  onClose?: () => void;
  setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
}

export const RcoSplitEditInspector = memo(function RcoSplitEditInspector({
  step,
  steps,
  setStep,
  onClose,
  setAutomation,
}: RcoSplitEditInspectorProps) {
  const user = useContext(UserContext);

  const isTrustedUser = useMemo(
    () => isTrustedAdminCheckoutOptimizationUser(user?.email),
    [user],
  );

  const [useStorefrontFeatures, setUseStorefrontFeatures] = useState(() => {
    return (
      step.treatments?.some((treatment) => !!treatment.storefrontFeatures) ??
      false
    );
  });

  const treatments = useMemo(() => {
    return step.treatments ?? [];
  }, [step.treatments]);

  const totalWeight = useMemo(() => {
    return treatments.reduce(
      (acc, treatment) => acc + (treatment.weight || 0),
      0,
    );
  }, [treatments]);

  const isWeightValid: SplitVariantControlsProps["isWeightValid"] =
    useMemo(() => {
      const firstTreatment = treatments[0];
      if (!firstTreatment) {
        return { isValid: false, errorMessage: "No variants found" };
      }

      const allTreatmentsHaveWeight = treatments.every(
        (treatment) =>
          treatment.weight && treatment.weight > 0 && treatment.weight <= 100,
      );
      if (!allTreatmentsHaveWeight) {
        return {
          isValid: false,
          errorMessage: "All variants must have a weight between 0% and 100%",
        };
      }

      const totalWeightIs100 = totalWeight === 100;
      if (totalWeightIs100) {
        return { isValid: true };
      }

      const allTreatmentsHaveSameWeight = treatments.every(
        (treatment) =>
          treatment.weight && treatment.weight === firstTreatment.weight,
      );
      if (allTreatmentsHaveSameWeight) {
        return { isValid: true };
      } else {
        return {
          isValid: false,
          errorMessage:
            "All variants must add up to 100% (or have the same weight)",
        };
      }
    }, [totalWeight, treatments]);

  const handleTreatmentNameChange = useHandler((id: string, name: string) => {
    const updatedTreatments = treatments.map((treatment) =>
      treatment.treatmentId === id ? { ...treatment, name } : treatment,
    );
    setStep({ ...step, treatments: updatedTreatments });
  });

  const handleAddTreatment = useHandler(() => {
    const newTreatment = {
      treatmentId: new ObjectId().toString(),
      name: "New variant",
      nextId: undefined,
      weight: 0,
    };
    setStep({ ...step, treatments: [...treatments, newTreatment] });
  });

  const handleRemoveTreatment = useHandler((id: string) => {
    const treatmentToRemove = treatments.find(
      (treatment) => treatment.treatmentId === id,
    );
    const updatedTreatments = treatments.filter(
      (treatment) => treatment.treatmentId !== id,
    );
    setStep({ ...step, treatments: updatedTreatments });

    // If we have setAutomation and the treatment has a nextId, delete all connected nodes
    if (setAutomation && treatmentToRemove?.nextId) {
      setAutomation((prevAutomation) => {
        if (!prevAutomation || !treatmentToRemove.nextId) {
          return prevAutomation;
        }

        const updatedSteps = deleteElementAndAllChildren(
          treatmentToRemove.nextId,
          prevAutomation.steps,
        );

        return { ...prevAutomation, steps: updatedSteps };
      });
    }
  });

  const handleTreatmentWeightChange = useHandler(
    (id: string, weightStr: string) => {
      const updatedTreatments = treatments.map((treatment) =>
        treatment.treatmentId === id
          ? { ...treatment, weight: Number(weightStr) }
          : treatment,
      );
      setStep({ ...step, treatments: updatedTreatments });
    },
  );

  const handleBalanceWeights = useHandler(() => {
    if (treatments && treatments.length > 0) {
      const equalWeight = Math.round(100 / treatments.length);

      const updatedTreatments = treatments.map((treatment) => ({
        ...treatment,
        weight: equalWeight,
      }));

      setStep({ ...step, treatments: updatedTreatments });
    }
  });

  const handleStorefrontFeaturesChange = useHandler(
    (
      treatmentId: string,
      storefrontFeatures: StorefrontFeatures | undefined,
    ) => {
      const updatedTreatments = treatments.map((treatment) =>
        treatment.treatmentId === treatmentId
          ? { ...treatment, storefrontFeatures }
          : treatment,
      );
      setStep({ ...step, treatments: updatedTreatments });
    },
  );

  const handleChangeUseStorefrontFeatures = useHandler((value: boolean) => {
    setUseStorefrontFeatures(value);

    if (!value) {
      const updatedTreatments = treatments.map(
        ({ storefrontFeatures, ...treatment }) => ({ ...treatment }),
      );
      setStep({ ...step, treatments: updatedTreatments });
    }
  });

  // Split can only have storefront features if it's the top node in the flow
  const splitCanUseStorefrontFeatures = useMemo(() => {
    const triggerStep = steps.find((s) => s.type === StepType.TRIGGER);

    if (!triggerStep) {
      return false;
    }

    // Check if the trigger's nextId points directly to our split step
    return triggerStep.nextId === step.id;
  }, [steps, step.id]);

  return (
    <Flex dir="column" gap="3xl" justify="flex-start">
      <Flex dir="row" justify="space-between">
        <Text fontSize="md" fontWeight="semibold">
          A/B test
        </Text>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={XIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>
      <Flex dir="column" gap="xl" justify="flex-start">
        <RedoTextInput
          fullWidth
          label="Test name"
          setValue={(value) => setStep({ ...step, name: value })}
          size="sm"
          value={step.name ?? ""}
        />
        <RedoTextareaInputField
          autoExpand
          fullWidth
          label="Hypothesis"
          setValue={(value) => setStep({ ...step, hypothesis: value })}
          value={step.hypothesis ?? ""}
        />
        <Flex dir="column" gap="xs" justify="flex-start">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Optimize for
          </Text>
          <RedoBadge
            color="blue"
            size="xs"
            text={
              CheckoutAbTestMetricToOptimizeLabelMap[
                step.metricToOptimize ??
                  CheckoutAbTestMetricToOptimize.CHECKOUT_CONVERSION_RATE
              ]
            }
          />
        </Flex>
        <Flex dir="column" gap="xs" justify="flex-start">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Variants
          </Text>
          {!!step.isActive && (
            <Text fontSize="xs" fontWeight="regular" textColor="warning">
              {isTrustedUser
                ? "This A/B test is active. Variants are normally locked, but you are a trusted user — proceed with caution."
                : "This A/B test is active. Variants cannot be edited."}
            </Text>
          )}
          <SplitVariantControls
            isWeightValid={isWeightValid}
            onAddVariant={handleAddTreatment}
            onBalanceWeights={handleBalanceWeights}
            onRemoveVariant={handleRemoveTreatment}
            onVariantNameChange={handleTreatmentNameChange}
            onVariantWeightChange={handleTreatmentWeightChange}
            readOnly={!!step.isActive && !isTrustedUser}
            totalWeight={totalWeight}
            variants={treatments.map((treatment) => ({
              id: treatment.treatmentId ?? "",
              name: treatment.name ?? "",
              weight: treatment.weight ?? 0,
            }))}
          />
        </Flex>
        <DisabledTooltip
          disabledMessage={
            splitCanUseStorefrontFeatures
              ? !isTrustedUser && !!step.isActive
                ? "This A/B test is active. Storefront features cannot be edited."
                : undefined
              : "Split can only have storefront features if it's the top node in the flow."
          }
        >
          <RedoToggle
            disabled={
              !splitCanUseStorefrontFeatures ||
              (!!step.isActive && !isTrustedUser)
            }
            label="Use storefront features"
            setValue={handleChangeUseStorefrontFeatures}
            size="sm"
            value={useStorefrontFeatures}
          />
        </DisabledTooltip>
        {useStorefrontFeatures &&
          step.treatments?.map((treatment) => {
            const storefrontFeaturesParseResult =
              checkoutOptimizationTreatmentStorefrontFeaturesSchema.safeParse(
                treatment.storefrontFeatures,
              );
            const storefrontFeatures = storefrontFeaturesParseResult.success
              ? storefrontFeaturesParseResult.data
              : undefined;

            return (
              <TreatmentStorefrontFeaturesConfiguration
                key={treatment.treatmentId}
                onStorefrontFeaturesChange={handleStorefrontFeaturesChange}
                readonly={false}
                storefrontFeatures={storefrontFeatures}
                treatmentId={treatment.treatmentId ?? ""}
                treatmentName={treatment.name ?? ""}
              />
            );
          })}
      </Flex>
    </Flex>
  );
});
